import { BusinessSkeleton } from '@/components/Skeleton'
import { getBusinessAndDayoffs } from '@/modules/Dashboard/services'
import { generatePathDatacentral, paths } from '@/utils/path'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button, Typogaphy } from 'vnetwork-ui'
import { motion, AnimatePresence } from 'framer-motion'
import BusinessCard from './BusinessCard'
import { Plus } from 'lucide-react'

import { QUERY_KEYS } from '@/constants/reactQueryKeys'

interface BusinessProps {
    showCreateButton?: boolean
    defaultBusinessType?: string
}

const Business: React.FC<BusinessProps> = ({
    showCreateButton = false,
    defaultBusinessType,
}) => {
    const { t } = useTranslation('dashboard')
    const navigate = useNavigate()
    const [showDropdown, setShowDropdown] = useState(false)

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [QUERY_KEYS.DASHBOARD_BUSINESSES],
            queryFn: () => getBusinessAndDayoffs(),
        },
        {
            loadingComponent: <BusinessSkeleton />,
            errorDisplayMode: 'page',
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    const handleViewAllBusiness = () => {
        navigate(generatePathDatacentral(paths.businessesManage, {}))
    }

    const handleViewAllDayoff = () => {
        navigate(generatePathDatacentral(paths.dayOffsManage, {}))
    }

    const handleCreateBusiness = () => {
        const url = defaultBusinessType
            ? `${paths.businessAccountCreate}?business_type=${defaultBusinessType}`
            : paths.businessAccountCreate
        navigate(url)
    }

    return (
        <div className="relative h-full">
            <div className="flex items-center justify-between mb-4">
                <Typogaphy className="font-semibold text-primary capitalize">
                    {t('business.title')}
                </Typogaphy>
                {showCreateButton && (
                    <Button
                        size="small"
                        variant="outlined"
                        className="!min-w-0 !px-2 !py-1"
                        onClick={handleCreateBusiness}
                    >
                        <Plus className="w-4 h-4" />
                    </Button>
                )}
            </div>
            {(() => {
                const todayList = data?.result?.today || []
                const tomorrowList = data?.result?.tomorrow || []

                // Both empty → single centered empty state (no split panes).
                if (todayList.length === 0 && tomorrowList.length === 0) {
                    return (
                        <div className="h-[370px] flex flex-col items-center justify-center text-center">
                            <div className="w-8 h-8 mx-auto mb-4 bg-gray-200 rounded-full flex items-center justify-center">
                                <svg
                                    className="w-4 h-4 text-gray-400"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={1}
                                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                                    />
                                </svg>
                            </div>
                            <Typogaphy className="text-gray-500 text-sm font-medium mb-2">
                                {t('business.no_data')}
                            </Typogaphy>
                            <Typogaphy className="text-gray-400 text-sm">
                                {t('business.no_data_description')}
                            </Typogaphy>
                        </div>
                    )
                }

                // Two independent scroll panes of equal height so "Ngày mai" is
                // always visible regardless of how many "Hôm nay" items there are.
                return (
                    <div className="h-[370px] flex flex-col gap-3">
                        {/* Hôm nay Section */}
                        <div className="flex-1 min-h-0 flex flex-col">
                            <Typogaphy className="font-semibold text-primary text-sm mb-2">
                                {t('business.today')}
                                {todayList.length > 0
                                    ? ` (${todayList.length})`
                                    : ''}
                            </Typogaphy>
                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                <div className="space-y-1">
                                    {todayList.length > 0 ? (
                                        todayList.map((business, index) => (
                                            <BusinessCard
                                                key={
                                                    business.employee
                                                        .employee_id
                                                }
                                                business={business}
                                                index={index}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-center py-4 bg-gray-50 rounded-md">
                                            <Typogaphy className="text-gray-500 text-sm">
                                                {t('business.no_data_today')}
                                            </Typogaphy>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Ngày mai Section */}
                        <div className="flex-1 min-h-0 flex flex-col">
                            <Typogaphy className="font-semibold text-primary text-sm mb-2">
                                {t('business.tomorrow')}
                                {tomorrowList.length > 0
                                    ? ` (${tomorrowList.length})`
                                    : ''}
                            </Typogaphy>
                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                                <div className="space-y-1">
                                    {tomorrowList.length > 0 ? (
                                        tomorrowList.map((business, index) => (
                                            <BusinessCard
                                                key={
                                                    business.employee
                                                        .employee_id
                                                }
                                                business={business}
                                                index={index}
                                            />
                                        ))
                                    ) : (
                                        <div className="text-center py-4 bg-gray-50 rounded-md">
                                            <Typogaphy className="text-gray-500 text-sm">
                                                {t('business.no_data_tomorrow')}
                                            </Typogaphy>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            })()}

            {/* View All Button - Outside scroll container */}
            {((data?.result?.today && data.result.today.length > 0) ||
                (data?.result?.tomorrow &&
                    data.result.tomorrow.length > 0)) && (
                <div className="mt-4 pt-3 border-secondary-20 absolute bottom-0 left-0 right-0">
                    <div
                        className="relative"
                        onMouseEnter={() => setShowDropdown(true)}
                        onMouseLeave={() => setShowDropdown(false)}
                    >
                        <AnimatePresence mode="wait">
                            {!showDropdown ? (
                                <motion.div
                                    key="single-button"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                    transition={{
                                        duration: 0.2,
                                        ease: 'easeInOut',
                                    }}
                                >
                                    <Button
                                        size="small"
                                        variant="text"
                                        className="w-full !bg-bg-secondary-10 text-primary transition-colors"
                                    >
                                        {t('business.view_all')}
                                    </Button>
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="double-buttons"
                                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                    animate={{ opacity: 1, y: 0, scale: 1 }}
                                    exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                    transition={{
                                        duration: 0.2,
                                        ease: 'easeInOut',
                                    }}
                                    className="flex gap-2"
                                >
                                    <motion.div
                                        initial={{ opacity: 0, x: -20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                            duration: 0.2,
                                            delay: 0.1,
                                            ease: 'easeOut',
                                        }}
                                        className="flex-1"
                                    >
                                        <Button
                                            size="small"
                                            variant="text"
                                            className="w-full !bg-bg-secondary-10 text-primary hover:bg-bg-secondary-10 transition-colors"
                                            onClick={handleViewAllBusiness}
                                        >
                                            {t('business.view_all_business')}
                                        </Button>
                                    </motion.div>
                                    <motion.div
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{
                                            duration: 0.2,
                                            delay: 0.1,
                                            ease: 'easeOut',
                                        }}
                                        className="flex-1"
                                    >
                                        <Button
                                            size="small"
                                            variant="text"
                                            className="w-full !bg-bg-secondary-10 text-primary hover:bg-bg-secondary-10 transition-colors"
                                            onClick={handleViewAllDayoff}
                                        >
                                            {t('business.view_all_dayoff')}
                                        </Button>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            )}
        </div>
    )
}

export default Business
